import React from 'react';
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from '@directv-digital/dds.ui.dtv-text';
import Tooltip from "@directv-digital/fusion.molecules.tooltip";
import { colorPalette } from '@directv-digital/dds.ui.dtv-theme';
import ErrorDisplay from "@directv-digital/dds.ui.error-display";

{ /* Generated by Copilot- Start */ }

const CHIP_CHAR_LIMIT_MOBILE = 16;
const CHIP_CHAR_LIMIT_TABLET = 17;
const CHIP_CHAR_LIMIT_DESKTOP = 25;

function getCharLimit(device: string) {
  if (device === 'mobile') {
    return CHIP_CHAR_LIMIT_MOBILE;
  }
  if (device === 'tablet') {
    return CHIP_CHAR_LIMIT_TABLET;
  }
  return CHIP_CHAR_LIMIT_DESKTOP;
}

export const GenrePackHeader = React.memo((props: any) => {
  const {
    device,
    index,
    columnSelections,
    genrePackages,
    miniSelectionsByColumn,
    miniPackages,
    packagesConfig,
    onAddMorePack,
    addMoreLabel
  } = props;

  const selectedGenreIds = columnSelections[index]?.genre || [];
  const selectedMiniIds = miniSelectionsByColumn?.[index] || [];
  const allSelectedIds = [...selectedGenreIds, ...selectedMiniIds];
  const selectionKey = allSelectedIds.join(',');

  // Generated by Copilot- Start - 2026-04-08 15:28:21
  const getDisplayName = React.useCallback(id => {
    const genreObj = (genrePackages || []).find(
      g => g.id === id || g.packageId === id || g.queryparam === id
    );
    if (genreObj) {
      return genreObj.packageName || genreObj.displayLabel || id;
    }

    const multiPackObj = (packagesConfig || []).find(
      p => (p.id === id || p.packageId === id || p.queryparam === id) &&
                p.subType === 'multi'
    );
    if (multiPackObj) {
      return multiPackObj.packageName || multiPackObj.displayLabel || id;
    }

    const miniObj = (miniPackages || []).find(
      m => m.id === id || m.packageId === id || m.queryparam === id
    );
    if (miniObj) {
      return miniObj.displayLabel || miniObj.packageName || id;
    }

    return id;
  }, [genrePackages, miniPackages, packagesConfig]);
    // Generated by Copilot- End - 2026-04-08 15:28:21

  const isLongName = React.useCallback((id: string) => {
    const name = getDisplayName(id) || '';
    const charLimit = getCharLimit(device);
    // Reduce limit by 2 for multi-word names
    const effectiveLimit = name.trim().includes(' ') ? charLimit - 2 : charLimit;
    return (
      name.split(' ').some(word => word.length > effectiveLimit) ||
            name.length > effectiveLimit
    );
  }, [getDisplayName, device]);

  const visibleCount = React.useMemo(() => {
    if (device === 'mobile') {
      const first = allSelectedIds[0];
      const second = allSelectedIds[1];
      if (!first) {
        return 0;
      }
      if (!second) {
        return 1;
      }
      if (isLongName(first) || isLongName(second)) {
        return 1;
      }
      return 2;
    }
    if (device === 'tablet') {
      const longCount = allSelectedIds.slice(0, 3).filter(isLongName).length;
      let count = 3;
      if (longCount >= 2) {
        count = 1;
      } else if (longCount === 1) {
        count = 2;
      }
      if (longCount > 0 && allSelectedIds.length > count) {
        count = Math.max(1, count - 1);
      }
      return count;
    }
    if (device === 'desktop') {
      const firstThreeLong = allSelectedIds.slice(0, 3).some(isLongName);
      return firstThreeLong ? 2 : 3;
    }
    return 3;
  }, [device, selectionKey, isLongName]);

  const showAddMore = allSelectedIds.length === 1;

  try {
    const isMobile = device === 'mobile';
    return (
            <DtvBox
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'row' : 'column',
                    alignItems: isMobile ? 'flex-end' : 'flex-start',
                    width: '100%',
                    justifyContent: isMobile ? 'center' : 'flex-start'
                }}
            >
                {/* Show visible chips */}
                {allSelectedIds.slice(0, visibleCount).map(id => (
                    <DtvText
                        content={getDisplayName(id)}
                        key={id}
                        variant={"body2medium"}
                        sx={{
                            textAlign: 'left',
                            borderRadius: 4,
                            px: 1,
                            fontSize: '16px !important',
                            mr: 0.5,
                            overflowWrap: 'anywhere',
                            whiteSpace: 'normal',
                            ...(isMobile ? {
                                writingMode: 'vertical-rl',
                                textOrientation: 'sideways',
                                transform: 'rotate(180deg)',
                                maxHeight: '120px'
                            } : {})
                        }}
                    />
                ))}

                {/* Tooltip for +N more */}
                {allSelectedIds.length > visibleCount && (
                    <Tooltip
                        enterTouchDelay={0}
                        fontSize="14px !important"
                        leaveTouchDelay={300}
                        placement="top-start"
                        PopperProps={{
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [-53, -3]
                                    }
                                }
                            ],
                            sx: {
                                "& .MuiTypography-root": {
                                    fontFamily: `roboto-regular`,
                                    fontSize: "14px"
                                }
                            }
                        }}
                        content={
                            allSelectedIds
                                .map(getDisplayName)
                                .join('\n')
                        }
                        tooltipStyles={{
                            backgroundColor: "#fff",
                            color: colorPalette.blue[900],
                            borderRadius: 8,
                            padding: "8px",
                            width: "auto",
                            minWidth: 137,
                            maxWidth: 300,
                            whiteSpace: 'pre-line',
                            boxShadow: "0 2px 20px 0 rgba(0,0,0,0.16)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            wordBreak: "break-word",
                            overflowWrap: "break-word"
                        }}
                    >
                        <DtvText
                            content={`+${allSelectedIds.length - visibleCount} more`}
                            variant={"body3medium"}
                            sx={{
                                fontWeight: 500,
                                fontSize: 13,
                                color: '#1976d2',
                                cursor: 'pointer',
                                display: 'block',
                                ...(isMobile ? {
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'sideways',
                                    transform: 'rotate(180deg)',
                                    mb: 0.5
                                } : { mt: 0.5 })
                            }}
                        />
                    </Tooltip>
                )}

                {/* Add more pack vertical button if only one pack is present */}
                {showAddMore && (
                    <DtvText
                        content={addMoreLabel}
                        variant={"body3medium"}
                        sx={{
                            color: '#1976d2',
                            cursor: 'pointer',
                            writingMode: isMobile ? 'vertical-rl' : 'horizontal-tb',
                            textOrientation: isMobile ? 'sideways' : 'mixed',
                            transform: isMobile ? 'rotate(180deg)' : 'none',
                            px: 0.5,
                            paddingTop: { sm: "4px" },
                            textAlign: 'left'
                        }}
                        onClick={() => onAddMorePack && onAddMorePack(index)}
                    />
                )}
            </DtvBox>
    );
  } catch (error) {
    console.error("Error in CustomerAcqCompareTool - GenrePackHeader component >> ", error);
    return <ErrorDisplay component="GenrePackHeader" error={error} />;
  }
});
{ /* Generated by Copilot- End */ }
